import { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import TurfCard from '../components/TurfCard';
import HowItWorks from '../components/HowItWorks';
import OffersSection from '../components/OffersSection';
import SportsLoader from '../components/SportsLoader';
import PlayStoreButton from '../components/PlayStoreButton';
import AppStoreButton from '../components/AppStoreButton';
import { useNavigate } from 'react-router-dom';
import { Search, Navigation, Trophy, MapPin, X } from 'lucide-react';
import { API_URL as API } from '../config/api';
import { useTheme } from '../context/ThemeContext';
import { getTournamentImage } from './Tournaments';
import heroVideo from '../assets/hero-section.mp4';
import backgroundImage from '../assets/background.webp';

const SPORTS = [
  { name: 'Football' },
  { name: 'Cricket' },
  { name: 'Badminton' },
  { name: 'Tennis' },
  { name: 'Basketball' },
];

const FILTER_SPORTS = ['All', 'Football', 'Cricket', 'Badminton'];

export default function Home() {
  const [turfs, setTurfs] = useState([]);
  const [tournaments, setTournaments] = useState([]);
  const [sport, setSport] = useState('All');
  const [loading, setLoading] = useState(false);
  const [showPageLoader, setShowPageLoader] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768);
  const searchRef = useRef(null);

  const navigate = useNavigate();
  const { colors } = useTheme();

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    axios.get(`${API}/turfs`)
      .then(res => {
        const turfData = Array.isArray(res.data) ? res.data : (res.data.turfs || []);
        const approved = turfData.filter(t => (t.status || '').toLowerCase() === 'active' && t.isActive !== false && t.is_active !== false);
        setTurfs(approved);
      })
      .catch(err => console.error('Failed to fetch turfs:', err));

    axios.get(`${API}/tournaments`)
      .then(res => {
        setTournaments(Array.isArray(res.data) ? res.data : []);
      })
      .catch(err => console.error('Failed to fetch tournaments:', err));
      
    if (window.location.hash === '#offers') {
      setTimeout(() => {
        document.getElementById('offers')?.scrollIntoView({ behavior: 'smooth' });
      }, 300);
    }

    const handleClickOutside = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchNearby = () => {
    setLoading(true);
    navigator.geolocation.getCurrentPosition(async pos => {
      const { latitude, longitude } = pos.coords;
      try {
        const res = await axios.get(`${API}/turfs/nearby?lat=${latitude}&lng=${longitude}&radius=10000`);
        const turfData = Array.isArray(res.data) ? res.data : (res.data.turfs || []);
        const approved = turfData.filter(t => (t.status || '').toLowerCase() === 'active' && t.isActive !== false && t.is_active !== false);
        setTurfs(approved);
      } catch (err) { console.error(err); }
      setLoading(false);
    }, () => { alert('Location access denied'); setLoading(false); });
  };

  const turfsList = Array.isArray(turfs) ? turfs : [];
  const filtered = sport === 'All'
    ? turfsList
    : turfsList.filter(t => (t.sport || '').toLowerCase() === sport.toLowerCase());

  const getSuggestions = () => {
    const q = searchQuery.trim().toLowerCase();
    if (q.length < 2) return [];

    const suggestions = [];
    const addedKeys = new Set();

    turfsList.forEach(t => {
      const name = t.name || '';
      const city = t.city || '';
      const location = t.location || t.address || '';

      // Match Turf Name
      if (name.toLowerCase().includes(q)) {
        const key = `turf-${t._id || name}`;
        if (!addedKeys.has(key)) {
          addedKeys.add(key);
          suggestions.push({
            type: 'turf',
            title: name,
            subtitle: `${location ? location + ', ' : ''}${city} • ${t.sport || 'Sports'}`,
            turfId: t._id,
            searchVal: name
          });
        }
      }

      // Match City Name
      if (city.toLowerCase().includes(q)) {
        const key = `city-${city.toLowerCase()}`;
        if (!addedKeys.has(key)) {
          addedKeys.add(key);
          suggestions.push({
            type: 'city',
            title: city,
            subtitle: 'City',
            searchVal: city
          });
        }
      }

      // Match Location / Place / Address
      if (location.toLowerCase().includes(q)) {
        const key = `loc-${location.toLowerCase()}`;
        if (!addedKeys.has(key)) {
          addedKeys.add(key);
          suggestions.push({
            type: 'place',
            title: location,
            subtitle: `Place in ${city || 'India'}`,
            searchVal: location
          });
        }
      }
    });

    return suggestions.slice(0, 8);
  };

  const handleSelectSuggestion = (s) => {
    setShowDropdown(false);
    if (s.type === 'turf' && s.turfId) {
      navigate(`/turf/${s.turfId}`);
    } else {
      navigate(`/explore?search=${encodeURIComponent(s.searchVal)}`);
    }
  };

  const handleSearchSubmit = (e) => {
    if (e) e.preventDefault();
    if (searchQuery.trim()) {
      setShowDropdown(false);
      navigate(`/explore?search=${encodeURIComponent(searchQuery.trim())}`);
    }
  };

  return (
    <div style={{ background: colors.background, fontFamily: "'DM Sans', sans-serif", overflowX: 'hidden', maxWidth: '100vw' }}>
      {showPageLoader && <SportsLoader durationMs={2500} onFinish={() => setShowPageLoader(false)} />}

      {/* HERO */}
      <div style={{
        background: '#084734',
        padding: isMobile ? '3.5rem 1rem 3rem' : '5rem 2rem 4rem',
        textAlign: 'center',
        color: 'white',
        position: 'relative',
        overflow: 'visible',
        minHeight: isMobile ? '440px' : '560px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
      }}>
        {/* Background Video */}
        <video
          autoPlay
          loop
          muted
          playsInline
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            zIndex: 0,
            opacity: 1,
            transform: 'scale(1.05)',
            pointerEvents: 'none',
          }}
        >
          <source src={heroVideo} type="video/mp4" />
        </video>

        {/* Video Overlay Tint */}
        <div style={{
          position: 'absolute',
          inset: 0,
          background: 'linear-gradient(to bottom, rgba(8, 71, 52, 0.3), rgba(8, 71, 52, 0.45))',
          zIndex: 1,
          pointerEvents: 'none',
        }} />

        {/* Grid pattern */}
        <div style={{
          position: 'absolute', inset: 0,
          backgroundImage: `
            linear-gradient(rgba(255,255,255,0.05) 1px, transparent 1px),
            linear-gradient(90deg, rgba(255,255,255,0.05) 1px, transparent 1px)
          `,
          backgroundSize: '60px 60px',
          pointerEvents: 'none',
          zIndex: 2,
        }} />

        {/* Content Wrapper */}
        <div style={{ position: 'relative', zIndex: 3, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', width: '100%', maxWidth: '800px' }}>

          <h1 style={{
            fontSize: isMobile ? '2.4rem' : '4.2rem', fontWeight: '900',
            marginBottom: '0.2rem', lineHeight: 1.05,
            letterSpacing: '-1px', color: 'white',
            fontFamily: "'Sora', sans-serif",
          }}>
            Book. Play. <span style={{ color: '#CEF17B' }}>Enjoy.</span>
          </h1>

          <p style={{
            fontSize: isMobile ? '0.95rem' : '1.1rem', color: 'rgba(255,255,255,0.85)',
            marginBottom: '1.25rem', maxWidth: '520px',
            fontWeight: '400', lineHeight: 1.6,
          }}>
            Find and reserve premium sports turfs near you — football, cricket, badminton & more.
          </p>

          {/* APP STORE & PLAY STORE DOWNLOAD BUTTONS ABOVE SEARCH BAR */}
          <div style={{ marginBottom: '1.75rem', display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
            <PlayStoreButton />
            <AppStoreButton />
          </div>

          {/* SEARCH BAR WITH DROPDOWN */}
          <div ref={searchRef} style={{ position: 'relative', width: '100%', maxWidth: '620px', margin: '0 auto 2rem', zIndex: 50, boxSizing: 'border-box' }}>
            <form onSubmit={handleSearchSubmit} style={{ display: 'flex', alignItems: 'center', background: 'rgba(255, 255, 255, 0.96)', backdropFilter: 'blur(12px)', borderRadius: '50px', padding: isMobile ? '4px 6px 4px 12px' : '6px 8px 6px 20px', boxShadow: '0 12px 35px rgba(0,0,0,0.3)', border: '1.5px solid rgba(255,255,255,0.5)', width: '100%', boxSizing: 'border-box' }}>
              <Search size={isMobile ? 18 : 20} color="#084734" style={{ minWidth: isMobile ? '18px' : '20px', marginRight: isMobile ? '8px' : '12px', flexShrink: 0 }} />
              <input
                type="text"
                placeholder={isMobile ? "Search turf, city, location..." : "Search address, turf name, city name or place..."}
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setShowDropdown(true);
                }}
                onFocus={() => setShowDropdown(true)}
                style={{
                  flex: 1,
                  minWidth: 0,
                  border: 'none',
                  outline: 'none',
                  background: 'transparent',
                  fontSize: isMobile ? '0.88rem' : '0.98rem',
                  color: '#161616',
                  fontWeight: '500',
                  padding: isMobile ? '8px 0' : '12px 0',
                }}
              />
              {searchQuery && (
                <button
                  type="button"
                  onClick={() => { setSearchQuery(''); setShowDropdown(false); }}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px', color: '#666', display: 'flex', alignItems: 'center', flexShrink: 0 }}
                >
                  <X size={16} />
                </button>
              )}
              <button
                type="submit"
                style={{
                  background: '#084734',
                  color: '#CEF17B',
                  border: 'none',
                  borderRadius: '50px',
                  padding: isMobile ? '8px 14px' : '12px 26px',
                  fontSize: isMobile ? '0.82rem' : '0.95rem',
                  fontWeight: '700',
                  cursor: 'pointer',
                  marginLeft: '4px',
                  flexShrink: 0,
                  whiteSpace: 'nowrap',
                  transition: 'all 0.2s',
                }}
              >
                Search
              </button>
            </form>

            {/* DROPDOWN SUGGESTIONS */}
            {showDropdown && searchQuery.trim().length >= 2 && (
              <div style={{
                position: 'absolute',
                top: 'calc(100% + 8px)',
                left: 0,
                right: 0,
                background: 'white',
                borderRadius: '16px',
                boxShadow: '0 20px 40px rgba(0,0,0,0.25)',
                border: '1px solid #E5E7EB',
                overflow: 'hidden',
                zIndex: 100,
                maxHeight: '340px',
                overflowY: 'auto',
                textAlign: 'left'
              }}>
                {getSuggestions().length > 0 ? (
                  getSuggestions().map((s, idx) => (
                    <div
                      key={idx}
                      onClick={() => handleSelectSuggestion(s)}
                      style={{
                        padding: '12px 20px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '14px',
                        cursor: 'pointer',
                        borderBottom: idx < getSuggestions().length - 1 ? '1px solid #F3F4F6' : 'none',
                        transition: 'background 0.15s',
                        color: '#1F2937'
                      }}
                      onMouseEnter={e => e.currentTarget.style.background = '#F9FAFB'}
                      onMouseLeave={e => e.currentTarget.style.background = 'white'}
                    >
                      <div style={{
                        width: '36px',
                        height: '36px',
                        borderRadius: '50%',
                        background: s.type === 'turf' ? '#E6F4EA' : s.type === 'city' ? '#FEF3C7' : '#E0F2FE',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: s.type === 'turf' ? '#084734' : s.type === 'city' ? '#D97706' : '#0284C7',
                        flexShrink: 0
                      }}>
                        {s.type === 'turf' ? <Trophy size={18} /> : s.type === 'city' ? <MapPin size={18} /> : <Navigation size={18} />}
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: '600', fontSize: '0.95rem', color: '#111827', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {s.title}
                        </div>
                        <div style={{ fontSize: '0.8rem', color: '#6B7280', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {s.subtitle}
                        </div>
                      </div>
                      <span style={{
                        fontSize: '0.72rem',
                        fontWeight: '700',
                        textTransform: 'uppercase',
                        padding: '3px 8px',
                        borderRadius: '12px',
                        background: s.type === 'turf' ? 'rgba(8,71,52,0.1)' : 'rgba(107,114,128,0.1)',
                        color: s.type === 'turf' ? '#084734' : '#6B7280'
                      }}>
                        {s.type}
                      </span>
                    </div>
                  ))
                ) : (
                  <div style={{ padding: '16px 20px', color: '#6B7280', fontSize: '0.9rem', textAlign: 'center' }}>
                    No matching turfs, cities, or locations found for "{searchQuery}"
                  </div>
                )}
              </div>
            )}
          </div>

          {/* CTA Buttons */}
          <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap', marginBottom: '2.5rem' }}>
            <button onClick={() => navigate('/explore')} style={{
              background: '#CEF17B', color: '#084734',
              border: 'none', padding: '14px 32px',
              borderRadius: '50px', fontSize: '0.95rem',
              fontWeight: '800', cursor: 'pointer',
              display: 'flex', alignItems: 'center', gap: '8px',
              transition: 'all 0.3s',
            }}
              onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 8px 25px rgba(206,241,123,0.4)'; }}
              onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = 'none'; }}
            >
              <Search size={18} /> View All Venues
            </button>
            <button onClick={fetchNearby} style={{
              background: 'transparent', color: 'white',
              border: '1.5px solid rgba(255,255,255,0.4)',
              padding: '14px 32px', borderRadius: '50px',
              fontSize: '0.95rem', fontWeight: '600', cursor: 'pointer',
              display: 'flex', alignItems: 'center', gap: '8px',
              transition: 'all 0.3s',
            }}
              onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.1)'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.7)'; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.4)'; }}
            >
              <Navigation size={18} /> {loading ? 'Locating...' : 'Venues Near Me'}
            </button>
          </div>

          {/* Sport Quick Select */}
          <div style={{ display: 'flex', gap: '10px', justifyContent: 'center', flexWrap: 'wrap' }}>
            {SPORTS.map(s => (
              <button key={s.name} onClick={() => { navigate(`/explore?sport=${s.name}`); }}
                style={{
                  background: 'rgba(255,255,255,0.08)',
                  border: '1px solid rgba(255,255,255,0.15)',
                  color: 'rgba(255,255,255,0.85)', padding: '10px 20px',
                  borderRadius: '50px', cursor: 'pointer',
                  fontSize: '0.9rem', fontWeight: '600',
                  transition: 'all 0.2s',
                }}
                onMouseEnter={e => { e.currentTarget.style.background = 'rgba(206,241,123,0.15)'; e.currentTarget.style.borderColor = '#CEF17B'; e.currentTarget.style.color = '#CEF17B'; }}
                onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.08)'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.15)'; e.currentTarget.style.color = 'rgba(255,255,255,0.85)'; }}
              >
                {s.name}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div style={{
        backgroundImage: `linear-gradient(rgba(255, 255, 255, 0.55), rgba(255, 255, 255, 0.55)), url(${backgroundImage})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        backgroundAttachment: 'fixed',
      }}>
        {/* ACTIVE & LIVE TOURNAMENTS SECTION */}
        {tournaments.length > 0 && (
          <div style={{ padding: '3.5rem 1.5rem 2rem', maxWidth: '1200px', margin: '0 auto', background: 'transparent' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: '1.5rem' }}>
              <div>
                <div style={{ fontSize: '0.75rem', fontWeight: '700', color: '#084734', textTransform: 'uppercase', letterSpacing: '2px', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{ display: 'inline-block', width: '20px', height: '2px', background: '#CEF17B' }}></span>
                  Compete & Win
                </div>
                <h2 style={{ fontSize: '2.2rem', fontWeight: '800', color: colors.text }}>
                  Active & Live <span style={{ color: '#084734' }}>Tournaments 🏆</span>
                </h2>
              </div>
              <button onClick={() => navigate('/tournaments')} style={{
                background: '#084734', color: '#CEF17B', border: 'none',
                padding: '10px 22px', borderRadius: '50px',
                fontSize: '0.88rem', fontWeight: '800', cursor: 'pointer',
                display: 'flex', alignItems: 'center', gap: '6px',
                boxShadow: '0 6px 18px rgba(8,71,52,0.25)'
              }}>
                View All Tournaments →
              </button>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: '1.25rem' }}>
              {tournaments.slice(0, 3).map(t => {
                const formatStr = t.tournament_type === 'Custom' ? t.tournament_type_custom : t.tournament_type;
                const sportStr = t.sport === 'Custom' ? t.sport_custom : t.sport;
                const isLive = t.status === 'live';

                return (
                  <div
                    key={t._id}
                    onClick={() => navigate('/tournaments')}
                    style={{
                      background: 'white', borderRadius: '20px', overflow: 'hidden',
                      border: '1.5px solid #E9EDE8', cursor: 'pointer',
                      boxShadow: '0 4px 16px rgba(0,0,0,0.04)',
                      transition: 'all 0.25s', display: 'flex', flexDirection: 'column', justifyContent: 'space-between'
                    }}
                    onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-3px)'; e.currentTarget.style.borderColor = '#084734'; }}
                    onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.borderColor = '#E9EDE8'; }}
                  >
                    {/* Banner Image / Gradient Banner Header */}
                    <div style={{ position: 'relative', width: '100%', height: '170px', overflow: 'hidden', background: 'linear-gradient(135deg, #084734 0%, #062C21 100%)' }}>
                      <img
                        src={getTournamentImage(t.image_url)}
                        alt={t.name}
                        onError={(e) => {
                          e.currentTarget.onerror = null;
                          e.currentTarget.src = 'https://images.unsplash.com/photo-1579952363873-27f3bade9f55?w=800&auto=format&fit=crop&q=80';
                        }}
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      />
                      {/* Floating Badges */}
                      <div style={{ position: 'absolute', top: '12px', left: '12px', right: '12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', zIndex: 2 }}>
                        <span style={{ background: isLive ? '#DC2626' : '#084734', color: isLive ? '#FFF' : '#CEF17B', fontSize: '0.7rem', fontWeight: '800', padding: '4px 12px', borderRadius: '20px', boxShadow: '0 2px 8px rgba(0,0,0,0.2)' }}>
                          {isLive ? '🔴 LIVE NOW' : '🟢 UPCOMING'}
                        </span>
                        <span style={{ fontSize: '0.75rem', fontWeight: '800', color: '#084734', background: 'rgba(255,255,255,0.92)', backdropFilter: 'blur(4px)', padding: '4px 10px', borderRadius: '20px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
                          {sportStr}
                        </span>
                      </div>
                    </div>

                    <div style={{ padding: '1.25rem', display: 'flex', flexDirection: 'column', flex: 1, justifyContent: 'space-between' }}>
                      <div>
                        <h3 style={{ fontSize: '1.2rem', fontWeight: '900', color: '#0D1F0F', marginBottom: '8px', lineHeight: 1.3 }}>
                          {t.name}
                        </h3>
                        <div style={{ fontSize: '0.82rem', color: '#4B5563', marginBottom: '10px' }}>
                          <MapPin size={14} color="#084734" style={{ display: 'inline', marginRight: '4px' }} />
                          <strong>{t.turf_name}</strong> · {t.address}
                        </div>

                        <div style={{ display: 'flex', flexDirection: 'column', gap: '3px', background: '#F8FAF7', padding: '6px 10px', borderRadius: '8px', border: '1px solid #EEF2E6', marginBottom: '10px', fontSize: '0.78rem' }}>
                          <div style={{ color: '#161616', fontWeight: '700' }}>
                            🗓️ <strong>Starts From:</strong> {t.start_date ? new Date(t.start_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : 'TBD'}
                          </div>
                          {t.end_date && (
                            <div style={{ color: '#4B5563' }}>
                              🏁 <strong>Ends On:</strong> {new Date(t.end_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                            </div>
                          )}
                        </div>

                        <div style={{ fontSize: '0.82rem', color: '#4B5563', marginBottom: '6px' }}>
                          Format: <strong>{formatStr} ({t.team_size}v{t.team_size})</strong> · {t.is_unlimited_teams ? 'Unlimited Teams' : `Max ${t.max_teams} Teams`}
                        </div>
                        <div style={{ fontSize: '0.78rem', color: '#374151', marginBottom: '10px' }}>
                          🎂 <strong>Age Limit:</strong> {t.is_open_age || (!t.min_age && !t.max_age) ? 'Open for All Ages' : `${t.min_age || 0} - ${t.max_age || 'Any'} Years`}
                        </div>
                        {t.prize_pool && (
                          <div style={{ background: '#FEF3C7', color: '#D97706', padding: '6px 10px', borderRadius: '8px', fontSize: '0.78rem', fontWeight: '800', marginBottom: '10px' }}>
                            🏆 Prize: {t.prize_pool}
                          </div>
                        )}
                      </div>

                      <div style={{ borderTop: '1px solid #F3F4F6', paddingTop: '10px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '10px' }}>
                        <span style={{ fontWeight: '900', fontSize: '1rem', color: '#084734' }}>
                          {t.entry_fee > 0 ? `₹${t.entry_fee} / team` : 'FREE ENTRY'}
                        </span>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate('/tournaments');
                          }}
                          style={{
                            background: '#084734',
                            color: '#FFFFFF',
                            border: 'none',
                            padding: '6px 14px',
                            borderRadius: '20px',
                            fontSize: '0.82rem',
                            fontWeight: '800',
                            cursor: 'pointer'
                          }}
                        >
                          View Details →
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* FEATURED TURFS */}
        <div style={{ padding: '2rem 1.5rem 4rem', maxWidth: '1200px', margin: '0 auto', background: 'transparent' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: '2rem' }}>
            <div>
              <div style={{ fontSize: '0.75rem', fontWeight: '700', color: '#084734', textTransform: 'uppercase', letterSpacing: '2px', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ display: 'inline-block', width: '20px', height: '2px', background: '#CEF17B' }}></span>
                Top rated near you
              </div>
              <h2 style={{ fontSize: '2.2rem', fontWeight: '800', color: colors.text }}>
                Featured <span style={{ color: '#084734' }}>Turfs</span>
              </h2>
            </div>
            <button onClick={() => navigate('/explore')} style={{
              background: 'none', border: 'none', color: '#084734',
              fontSize: '0.95rem', fontWeight: '700', cursor: 'pointer',
              display: 'flex', alignItems: 'center', gap: '6px',
            }}
              onMouseEnter={e => e.currentTarget.style.color = '#CEF17B'}
              onMouseLeave={e => e.currentTarget.style.color = '#084734'}
            >
              View All Venues →
            </button>
          </div>

          {/* Sport Filter */}
          <div style={{ display: 'flex', gap: '10px', marginBottom: '2rem', flexWrap: 'wrap' }}>
            {FILTER_SPORTS.map(s => (
              <button key={s} onClick={() => setSport(s)} style={{
                background: sport === s ? '#161616' : colors.hover,
                color: sport === s ? 'white' : colors.text,
                border: `1.5px solid ${sport === s ? '#161616' : colors.border}`,
                padding: '8px 20px', borderRadius: '50px',
                fontSize: '0.88rem', fontWeight: '600', cursor: 'pointer',
                transition: 'all 0.2s',
              }}>
                {s}
              </button>
            ))}
          </div>

          {filtered.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '4rem', color: colors.textSecondary }}>
              <MapPin size={48} style={{ margin: '0 auto 1rem', display: 'block', opacity: 0.3 }} />
              <p style={{ fontSize: '1.1rem', fontWeight: '500' }}>No venues match your current selection.</p>
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '1.2rem' }}>
              {filtered.slice(0, 6).map(turf => <TurfCard key={turf._id} turf={turf} />)}
            </div>
          )}
        </div>

        {/* HOW IT WORKS */}
        <HowItWorks />

        {/* OFFERS */}
        <div id="offers" style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 2rem', background: 'transparent' }}>
          <OffersSection />
        </div>
      </div>
    </div>
  );
}
